import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faCloudRain,
  faExclamationTriangle,
  faPeopleGroup,
  faSeedling,
  faSpinner,
  faSun,
  faTruck,
  faTruckFast,
  faUserShield,
  faUserTie,
} from '@fortawesome/pro-solid-svg-icons';

import {ErrorBanner} from '../../../shared/components';
import {TopRiskRow} from '../components';

interface IFarmRiskCatalogueItem {
  id: number;
  name: string;
}

interface IFarmRiskSelection {
  problem: string;
  mitigation: string;
}

interface IRiskDraft {
  problem: string;
  mitigation: string;
}

const RISK_ICON_MAP: Record<string, React.ReactElement> = {
  weather: <FontAwesomeIcon icon={faCloudRain} />,
  drought: <FontAwesomeIcon icon={faSun} />,
  'supply risk': <FontAwesomeIcon icon={faTruck} />,
  'yield risk': <FontAwesomeIcon icon={faSeedling} />,
  'transport risk': <FontAwesomeIcon icon={faTruckFast} />,
  'customer risk': <FontAwesomeIcon icon={faUserShield} />,
  'key man risk': <FontAwesomeIcon icon={faUserTie} />,
  'team related risk': <FontAwesomeIcon icon={faPeopleGroup} />,
};

export interface ITopRisksSubStepProps {
  topRisksError: string | null;
  isLoadingTopRisks: boolean;
  selectedRiskCount: number;
  sortedRiskCatalogue: IFarmRiskCatalogueItem[];
  selectionByRiskId: Map<number, IFarmRiskSelection>;
  perRiskSaving: Record<number, boolean>;
  riskDrafts: Record<number, IRiskDraft>;
  confirmDeleteByRiskId: Record<number, boolean>;
  riskErrors: Record<number, string>;
  hasAttemptedTopRisksBootstrap: boolean;
  hasApplicationId: boolean;
  onRiskDraftChange: (
    riskId: number,
    field: keyof IRiskDraft,
    value: string,
  ) => void;
  onDeleteRiskSelection: (risk: IFarmRiskCatalogueItem) => void | Promise<void>;
  onConfirmDeleteRisk: (riskId: number) => void;
  onPersistRiskSelection: (
    risk: IFarmRiskCatalogueItem,
    draft: IRiskDraft,
  ) => void | Promise<void>;
  onResetRiskDraftForRow: (risk: IFarmRiskCatalogueItem) => void;
}

export const TopRisksSubStep: React.FC<ITopRisksSubStepProps> = ({
  topRisksError,
  isLoadingTopRisks,
  selectedRiskCount,
  sortedRiskCatalogue,
  selectionByRiskId,
  perRiskSaving,
  riskDrafts,
  confirmDeleteByRiskId,
  riskErrors,
  hasAttemptedTopRisksBootstrap,
  hasApplicationId,
  onRiskDraftChange,
  onDeleteRiskSelection,
  onConfirmDeleteRisk,
  onPersistRiskSelection,
  onResetRiskDraftForRow,
}) => (
  <div className="top-risks-step">
    <header className="top-risks-step__intro">
      <h3>Add your top risks</h3>
      <p>
        Choose a risk, describe the issue, and outline how you plan to address
        it. This helps our team understand your preparedness and planning.
      </p>
    </header>

    <ErrorBanner message={topRisksError} />

    <div className="top-risks-step__status" aria-live="polite">
      {isLoadingTopRisks ? (
        <span className="top-risks-step__status-message">
          <FontAwesomeIcon icon={faSpinner} spin role="status" /> Loading risk
          catalogue...
        </span>
      ) : (
        <span className="top-risks-step__status-message">
          {selectedRiskCount > 0
            ? `${selectedRiskCount} ${
                selectedRiskCount === 1 ? 'risk' : 'risks'
              } captured`
            : 'No risks captured yet'}
        </span>
      )}
    </div>

    <div
      className="top-risks-step__table"
      role="list"
      aria-label="Risk catalogue"
    >
      <div className="top-risks-step__table-head" aria-hidden="true">
        <span>Risk</span>
        <span>Problem</span>
        <span>Mitigation</span>
        <span>Actions</span>
      </div>

      {sortedRiskCatalogue.map((risk) => {
        const riskId = Number(risk.id);
        const selection = selectionByRiskId.get(riskId) ?? null;
        const saving = Boolean(perRiskSaving[riskId]);
        const draft = riskDrafts[riskId] ?? {
          problem: selection?.problem ?? '',
          mitigation: selection?.mitigation ?? '',
        };
        const icon = RISK_ICON_MAP[risk.name.toLowerCase()] ??
          RISK_ICON_MAP['key man risk'] ?? (
            <FontAwesomeIcon icon={faExclamationTriangle} />
          );
        const isDeleteConfirming = Boolean(confirmDeleteByRiskId[riskId]);
        const cardError = riskErrors[riskId];
        const hasUnsavedChanges =
          draft.problem.trim() !== (selection?.problem ?? '').trim() ||
          draft.mitigation.trim() !== (selection?.mitigation ?? '').trim();
        const canReset =
          !saving &&
          !isLoadingTopRisks &&
          (hasUnsavedChanges || Boolean(cardError));

        return (
          <TopRiskRow
            key={risk.id}
            risk={risk}
            selection={selection}
            saving={saving}
            draft={draft}
            icon={icon}
            isDeleteConfirming={isDeleteConfirming}
            cardError={cardError}
            canReset={canReset}
            isLoadingTopRisks={isLoadingTopRisks}
            onRiskDraftChange={onRiskDraftChange}
            onDeleteClick={(selectedRisk, confirmingDelete) => {
              if (confirmingDelete) {
                void onDeleteRiskSelection(selectedRisk);
                return;
              }
              onConfirmDeleteRisk(Number(selectedRisk.id));
            }}
            onPersist={(selectedRisk, selectedDraft) => {
              void onPersistRiskSelection(selectedRisk, selectedDraft);
            }}
            onReset={onResetRiskDraftForRow}
          />
        );
      })}
    </div>

    {hasAttemptedTopRisksBootstrap && !hasApplicationId ? (
      <ErrorBanner
        message={
          'We could not determine your application. Please return to the previous step and try again.'
        }
      />
    ) : null}
  </div>
);
